import { CommonModule } from '@angular/common';
import { Component, ElementRef, HostListener, inject, Input, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { NgbActiveModal } from '@ng-bootstrap/ng-bootstrap';
import { ToastrService } from 'ngx-toastr';
import { Subject } from 'rxjs';
import { FlatpickrDatePickerComponent } from '../../../../../../_shared/components/flatpickr-date-picker/flatpickr-date-picker.component';
import { ErrorHandlerService } from '../../../../../../_shared/services/error-handler.service';
import { VehicleDocumentsService } from '../../../../services/vehicle-documents.service';
import { AssetCompliance, VehicleDocument, VehicleDocumentType } from '../../../../models/vehicle-documents.model';
import { TranslocoDirective, TranslocoPipe, TranslocoService } from '@jsverse/transloco';
import { translateLookupLabel } from '../../../../../../_shared/helpers/lookup-label.helper';

@Component({
  selector: 'app-edit-compliance-document-modal',
  standalone: true,
  imports: [CommonModule, FlatpickrDatePickerComponent, TranslocoDirective, TranslocoPipe],
  templateUrl: './edit-compliance-document-modal.component.html',
  styleUrl: './edit-compliance-document-modal.component.scss'
})
export class EditComplianceDocumentModalComponent implements OnInit, OnDestroy {
  @Input() document!: VehicleDocument;
  @Input() assetId!: number;

  @ViewChild('complianceControl') complianceControl?: ElementRef<HTMLElement>;

  complianceOptions: AssetCompliance[] = [];
  documentTypes: VehicleDocumentType[] = [];
  selectedComplianceIds: number[] = [];

  file: File | null = null;
  fileName = '';
  reference = '';
  expiryDate = '';

  isLoadingOptions = true;
  isUploadingTemp = false;
  uploadSuccess = false;
  uploadProgress = 0;
  tempUploadFailed = false;
  isSaving = false;

  complianceDropdownOpen = false;

  private readonly maxFileSizeInBytes = 5 * 1024 * 1024;
  private uploadInterval?: ReturnType<typeof setInterval>;

  private activeModal = inject(NgbActiveModal);
  private vehicleDocumentsService = inject(VehicleDocumentsService);
  private errorHandlerService = inject(ErrorHandlerService);
  private toastr = inject(ToastrService);
  private readonly transloco: TranslocoService = inject(TranslocoService);
  private cancellation = new Subject<void>();

  ngOnInit(): void {
    this.expiryDate = this.document.validUntil ? this.document.validUntil.split('T')[0] : '';
    this.loadOptions();
  }

  ngOnDestroy(): void {
    this.clearUploadInterval();
    this.cancellation.next();
    this.cancellation.complete();
  }

  @HostListener('document:click', ['$event'])
  onDocumentClick(event: MouseEvent): void {
    const target = event.target as Node;
    if (this.complianceDropdownOpen && this.complianceControl && !this.complianceControl.nativeElement.contains(target)) {
      this.complianceDropdownOpen = false;
    }
  }

  private get documentType(): VehicleDocumentType | undefined {
    return this.documentTypes.find(type => type.id === this.document.documentTypeId);
  }

  /** Display only: "Other" documents carry a free-text name, which the helper leaves as is. */
  get documentTypeName(): string {
    return translateLookupLabel(
      this.transloco,
      'assetOverview.documentTypeNames',
      this.documentType?.name ?? this.document.documentName
    );
  }

  get currentDocumentName(): string {
    return `${this.document.documentName}${this.document.extension ?? ''}`;
  }

  get expiryRequired(): boolean {
    return this.documentType?.validUntilRequired ?? false;
  }

  get selectedCompliances(): AssetCompliance[] {
    return this.complianceOptions.filter(compliance => this.selectedComplianceIds.includes(compliance.complianceId));
  }

  get canSave(): boolean {
    return this.selectedComplianceIds.length > 0
      && this.uploadSuccess
      && (!this.expiryRequired || !!this.expiryDate)
      && !this.isSaving;
  }

  toggleComplianceDropdown(): void {
    if (this.isLoadingOptions) return;
    this.complianceDropdownOpen = !this.complianceDropdownOpen;
  }

  isComplianceSelected(compliance: AssetCompliance): boolean {
    return this.selectedComplianceIds.includes(compliance.complianceId);
  }

  toggleCompliance(compliance: AssetCompliance): void {
    if (this.selectedComplianceIds.includes(compliance.complianceId)) {
      this.selectedComplianceIds = this.selectedComplianceIds.filter(id => id !== compliance.complianceId);
    } else {
      this.selectedComplianceIds = [...this.selectedComplianceIds, compliance.complianceId];
    }
  }

  onPillListWheel(event: WheelEvent): void {
    const list = event.currentTarget as HTMLElement;
    if (list.scrollWidth <= list.clientWidth || event.deltaY === 0) return;

    list.scrollLeft += event.deltaY;
    event.preventDefault();
  }

  onExpiryChange(value: string | undefined): void {
    this.expiryDate = value ?? '';
  }

  onFileSelected(event: Event): void {
    const input = event.target as HTMLInputElement;
    if (!input.files || input.files.length === 0) return;

    const selectedFile = input.files[0];
    if (selectedFile.size > this.maxFileSizeInBytes) {
      this.toastr.error(this.transloco.translate('assetOverview.editDocumentModal.toasts.fileTooLarge'));
      input.value = '';
      return;
    }

    this.file = selectedFile;
    this.fileName = selectedFile.name;
    this.reference = crypto.randomUUID();
    input.value = '';

    this.uploadTempDocument();
  }

  removeFile(): void {
    this.clearUploadInterval();
    this.file = null;
    this.fileName = '';
    this.reference = '';
    this.uploadProgress = 0;
    this.isUploadingTemp = false;
    this.uploadSuccess = false;
    this.tempUploadFailed = false;
  }

  async save(): Promise<void> {
    if (!this.canSave) return;

    this.isSaving = true;

    try {
      await this.vehicleDocumentsService.updateDocument({
        documentId: this.document.documentId,
        assetId: this.assetId,
        reference: this.reference,
        documentName: this.document.documentName,
        extension: this.getFileExtension(),
        documentTypeId: this.document.documentTypeId,
        validUntil: this.expiryDate || null,
        complianceIds: this.selectedComplianceIds
      });

      this.toastr.success(this.transloco.translate('assetOverview.editDocumentModal.toasts.updated'));
      this.activeModal.close(true);
      this.isSaving = false;
    } catch (error: any) {
      this.errorHandlerService.handleError(error, {
        showToast: true,
        callback: () => {
          this.isSaving = false;
        }
      });
    }
  }

  downloadDocument(): void {
    const link = this.document.downloadUrl;
    if (!link) return;

    const anchor = document.createElement('a');
    anchor.href = link;
    anchor.download = this.currentDocumentName;
    anchor.target = '_blank';
    anchor.rel = 'noopener';
    document.body.appendChild(anchor);
    anchor.click();
    document.body.removeChild(anchor);
  }

  closeModal(): void {
    this.activeModal.dismiss();
  }

  private async loadOptions(): Promise<void> {
    this.isLoadingOptions = true;

    try {
      const [compliancesResponse, documentTypesResponse, documentCompliancesResponse] = await Promise.all([
        this.vehicleDocumentsService.getAssetCompliances(this.assetId, this.cancellation),
        this.vehicleDocumentsService.getDocumentTypesByAssetId(this.assetId, this.cancellation),
        this.vehicleDocumentsService.getDocumentCompliances(this.document.documentId, this.cancellation)
      ]);

      this.complianceOptions = compliancesResponse.data ?? [];
      this.documentTypes = documentTypesResponse.data ?? [];
      this.selectedComplianceIds = documentCompliancesResponse.data ?? [];
      this.isLoadingOptions = false;
    } catch (error: any) {
      this.errorHandlerService.handleError(error, {
        showToast: true,
        callback: () => {
          this.isLoadingOptions = false;
        }
      });
    }
  }

  private uploadTempDocument(): void {
    if (!this.file) return;

    this.isUploadingTemp = true;
    this.uploadSuccess = false;
    this.tempUploadFailed = false;
    this.uploadProgress = 0;

    const formData = new FormData();
    formData.append('AssetId', this.assetId.toString());
    formData.append('Reference', this.reference);
    formData.append('Document', this.file);

    this.vehicleDocumentsService.uploadTempDocument(formData)
      .then(() => {
        this.uploadProgress = 100;
        setTimeout(() => {
          this.clearUploadInterval();
          this.isUploadingTemp = false;
          this.uploadSuccess = true;
        }, 500);
      })
      .catch((error: any) => {
        this.clearUploadInterval();
        this.tempUploadFailed = true;
        this.uploadProgress = 0;
        this.errorHandlerService.handleError(error, {
          showToast: true,
          callback: () => {
            this.isUploadingTemp = false;
          }
        });
      });

    this.uploadInterval = setInterval(() => {
      if (this.uploadProgress < 90) {
        this.uploadProgress += 1;
      }
    }, 100);
  }

  private clearUploadInterval(): void {
    if (this.uploadInterval) {
      clearInterval(this.uploadInterval);
      this.uploadInterval = undefined;
    }
  }

  private getFileExtension(): string {
    const parts = this.fileName.split('.');
    return parts.length > 1 ? `.${parts.pop()}` : '';
  }
}
